import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { Store } from '@ngrx/store';
import {
  PlatformDashboardLoadRequest,
} from '../../../store/platform-dashboard/platform-dashboard.actions';
import type {
  PlatformDashboardChannelStat,
  PlatformDashboardData,
} from '../../../store/platform-dashboard/platform-dashboard.models';
import {
  selectPlatformDashboardData,
  selectPlatformDashboardError,
  selectPlatformDashboardLoading,
} from '../../../store/platform-dashboard/platform-dashboard.selectors';

@Component({
  selector: 'app-super-admin-dashboard',
  standalone: true,
  imports: [CommonModule, MatButtonModule],
  templateUrl: './super-admin-dashboard.html',
  styleUrl: './super-admin-dashboard.scss',
})
export class SuperAdminDashboard {
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly loading = signal(true);
  protected readonly errorMessage = signal('');
  protected readonly dashboard = signal<PlatformDashboardData | null>(null);

  protected readonly usageMax = computed(() => {
    const maxValue = Math.max(...(this.dashboard()?.usage ?? []).map((point) => point.active_users), 0);
    return maxValue > 0 ? maxValue : 1;
  });

  constructor() {
    this.store.select(selectPlatformDashboardData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dashboard) => {
        this.dashboard.set(dashboard);
      });

    this.store.select(selectPlatformDashboardLoading)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loading) => {
        this.loading.set(!!loading);
      });

    this.store.select(selectPlatformDashboardError)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((error) => {
        this.errorMessage.set(error || '');
      });

    this.loadDashboard();
  }

  protected loadDashboard(): void {
    this.store.dispatch(PlatformDashboardLoadRequest());
  }

  protected usageBarHeight(value: number): string {
    const maxValue = this.usageMax();
    const height = Math.max((value / maxValue) * 100, value > 0 ? 12 : 4);
    return `${Math.min(height, 100)}%`;
  }

  protected formatCount(value: number | null | undefined): string {
    return new Intl.NumberFormat().format(value ?? 0);
  }

  protected formatChannelName(channel: PlatformDashboardChannelStat): string {
    return channel.channel_friendly_name?.trim() || channel.channel_id;
  }

  protected formatDate(value?: string | null): string {
    if (!value) {
      return 'No activity yet';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return 'No activity yet';
    }

    return parsed.toLocaleString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
